import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { DashboardDepartmentChangeService } from 'src/app/core/services/dashboard-department-change.service';
import { MaintenanceJobStatus } from 'src/app/maintenance-user/enums/maintenance-job-status.enum';
import { IMaintenanceJobStatusResponse } from 'src/app/maintenance-user/models/maintenance-job-status-response.model';
import { LoadMaintenanceJobStatus } from 'src/app/maintenance-user/state/maintenance-job.actions';
import { MaintenanceJobState } from 'src/app/maintenance-user/state/maintenance-job.state';
import { getDepartmentId } from 'src/app/services/store-snapshot.service';
import { TimePeriod } from 'src/app/shared/dashboard/models/time-period.enum';
import { getDashboardDateRange } from 'src/app/shared/functions/get-dashboard-date-range';
import {
  WidgetBlock,
  WidgetBlockClickEvent,
} from 'src/app/shared/ui-elements/ui-widget-card/ui-widget-card.component';
import { ChangeCurrentSelectedDepartment } from 'src/app/tenant-selector/state/tenant-user-access.actions';
import { TenantUserAccessState } from 'src/app/tenant-selector/state/tenant-user-access.state';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';
import { UserMaintenanceRoutes } from '../../enums/routes.enum';

@Component({
  selector: 'app-maintenance-dashboard-widgets',
  templateUrl: './maintenance-dashboard-widgets.component.html',
  styleUrls: ['./maintenance-dashboard-widgets.component.scss'],
  standalone: false,
})
export class MaintenanceDashboardWidgetsComponent implements OnInit, OnDestroy {
  expiredJobsData: WidgetBlock[] = [
    { name: 'expiredJobs', value: 0 },
    { name: 'critical', value: 0, icon: 'error_outline', iconColor: 'red' },
  ];

  dueSoonJobsData: WidgetBlock[] = [
    { name: 'dueSoonJobs', value: 0 },
    { name: 'critical', value: 0, icon: 'warning_amber', iconColor: 'yellow' },
  ];

  postponedJobs = 0;
  inProgressJobs = 0;
  completedJobs = 0;

  loadingActions = [LoadMaintenanceJobStatus];

  vesselControl = new FormControl<string>('', { nonNullable: true });
  vesselOptions = select(TenantUserAccessState.getAllAvailableDepartments);

  periodControl = new FormControl(TimePeriod.Last30Days);
  customRangeControl = new FormControl<{ from: Date; to: Date } | null>(null);

  private subscription = new Subscription();

  constructor(
    private store: Store,
    private router: Router,
    private dashboardDepartmentChangeService: DashboardDepartmentChangeService,
    private tenantUserPrivileges: TenantUserPrivilegesService
  ) {}

  ngOnInit(): void {
    this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
    this.listenToStatusChanges();
    this.loadMaintenanceJobs();

    this.subscription.add(
      this.vesselControl.valueChanges.subscribe((departmentId) => {
        this.dashboardDepartmentChangeService.setDashboardDepartment(departmentId);
        this.loadMaintenanceJobs();
      })
    );

    this.subscription.add(
      this.periodControl.valueChanges.subscribe((period) => {
        if (period === TimePeriod.Custom) {
          return;
        }

        this.clearCustomRangeFilters();
        this.loadMaintenanceJobs();
      })
    );

    this.subscription.add(
      this.customRangeControl.valueChanges.subscribe((range) => {
        this.handleCustomRangeChange(range);
      })
    );

    // Reload data when tenant changes
    this.subscription.add(
      this.store
        .select(TenantUserAccessState.currentTenant)
        .pipe(
          filter((tenant) => !!tenant),
          distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
          skip(1) // Skip the initial emission to avoid duplicate loads on component init
        )
        .subscribe(() => {
          // Reset filters when tenant changes
          this.vesselControl.setValue(getDepartmentId(), { emitEvent: false });
          this.periodControl.setValue(TimePeriod.Last30Days, { emitEvent: false });
          this.clearCustomRangeFilters();
          // Note: Data reload happens in department change handler (tenant change triggers department change)
        })
    );

    // Reload data when department changes
    this.subscription.add(
      this.dashboardDepartmentChangeService.getDepartmentChanges().subscribe((departmentId) => {
        this.syncDepartment(departmentId);
      })
    );

    this.subscription.add(
      this.dashboardDepartmentChangeService
        .getDashboardDepartmentChanges()
        .subscribe((departmentId) => {
          this.syncDepartment(departmentId);
        })
    );
  }

  private listenToStatusChanges(): void {
    this.subscription.add(
      this.store
        .select(MaintenanceJobState.getMaintenanceJobStatus)
        .subscribe((status: IMaintenanceJobStatusResponse | undefined) => {
          if (!status) {
            return;
          }

          this.updateWidgetData(status);
        })
    );
  }

  private syncDepartment(departmentId: string): void {
    if (this.vesselControl.value === departmentId) {
      return;
    }

    this.vesselControl.setValue(departmentId, { emitEvent: false });
    this.loadMaintenanceJobs();
  }

  private updateWidgetData(status: IMaintenanceJobStatusResponse): void {
    this.expiredJobsData = [
      {
        name: 'expiredJobs',
        value: status.expiredJobs,
        tooltip: 'expiredJobsTooltip',
      },
      {
        name: 'critical',
        value: status.expiredJobsCritical,
        icon: 'error_outline',
        iconColor: 'red',
        tooltip: 'expiredJobsCriticalTooltip',
      },
    ];

    this.dueSoonJobsData = [
      {
        name: 'dueSoonJobs',
        value: status.expiringJobs,
        tooltip: 'dueSoonJobsTooltip',
      },
      {
        name: 'critical',
        value: status.expiringJobsCritical,
        icon: 'warning_amber',
        iconColor: 'yellow',
        tooltip: 'dueSoonJobsCriticalTooltip',
      },
    ];

    this.postponedJobs = status.postponedJobs;
    this.inProgressJobs = status.inProgressJobs;
    this.completedJobs = status.completedJobs;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private handleCustomRangeChange(range: { from: Date; to: Date } | null): void {
    if (range) {
      this.periodControl.setValue(TimePeriod.Custom, { emitEvent: false });
      this.loadMaintenanceJobs();
    }
  }

  private clearCustomRangeFilters(): void {
    this.customRangeControl.setValue(null, { emitEvent: false });
  }

  private loadMaintenanceJobs(): void {
    if (!this.tenantUserPrivileges.userHasPrivilege(Privileges.MaintenanceJobRead)) {
      return;
    }

    const departmentId = this.vesselControl.value;
    const dateRange = getDashboardDateRange(
      this.periodControl.value || TimePeriod.Last30Days,
      this.customRangeControl.value
    );

    this.store.dispatch(new LoadMaintenanceJobStatus([departmentId], dateRange.from, dateRange.to));
  }

  onExpiredJobsBlockClick(event: WidgetBlockClickEvent): void {
    const isCritical = event.blockIndex === 1;
    this.navigateToJobsWithStatus(MaintenanceJobStatus.Expired, isCritical);
  }

  onDueSoonJobsBlockClick(event: WidgetBlockClickEvent): void {
    const isCritical = event.blockIndex === 1;
    this.navigateToJobsWithStatus(MaintenanceJobStatus.Expiring, isCritical);
  }

  openInProgressJobs(): void {
    this.navigateToJobsWithStatus(MaintenanceJobStatus.InProgress, false);
  }

  private navigateToJobsWithStatus(alertStatus: string, isCritical: boolean): void {
    const selectedDepartmentId = this.vesselControl.value;
    const currentDepartmentId = this.store.selectSnapshot(
      TenantUserAccessState.currentDepartmentId
    );

    if (selectedDepartmentId && selectedDepartmentId !== currentDepartmentId) {
      this.store.dispatch(new ChangeCurrentSelectedDepartment(selectedDepartmentId, false));
    }

    // Navigate to the maintenance list with the alert status filter and optional critical filter
    const queryParams: any = { alertStatus };
    if (isCritical) {
      queryParams.isCriticalComponent = 'true';
    }

    this.router.navigate([UserMaintenanceRoutes.Main], { queryParams });
  }
}
